import React from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps } from '@ds/react-utils'
import { PebbleErrorOlive, StatusErrorSmall } from '@ds/icons'
import { IconWithTheme } from '../../internal/components/IconWithTheme/IconWithTheme'
import { useDsUiDataProps, useThemeStyles, useThemeTokens } from '../../theming'
import type { AriaAttributes } from '../../types'
import styles from './styles'

export const inputDescriptionKinds = ['error', 'helper'] as const
export type InputDescriptionKind = (typeof inputDescriptionKinds)[number]

export interface InputDescriptionProps extends AriaAttributes {
  /**
   * Accepts aria attributes.
   */
  'aria-.*'?: string
  /** The message displayed in the InputDescription. */
  children: React.ReactChild
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /** Adds disabled styling to the InputDescription. */
  disabled?: boolean
  /** The 'id' of the associated control. */
  id: string
  /** The kind of message displayed. Either "error" or "helper". */
  kind?: InputDescriptionKind
}

export function InputDescription({
  children,
  'data-qa': dataQa,
  disabled = false,
  id,
  kind = 'helper',
  ...restProps
}: InputDescriptionProps) {
  const sx = useThemeStyles(styles, {
    children,
    id,
    kind,
  })
  const tokens = useThemeTokens()
  const dsUiDataProps = useDsUiDataProps(InputDescription)

  const iconNode = kind === 'error' && (
    <IconWithTheme
      inkIcon={
        <StatusErrorSmall
          data-qa={dataQa && `${dataQa}-error-icon`}
          fill={tokens.iconColorError}
        />
      }
      oliveIcon={
        <PebbleErrorOlive
          data-qa={dataQa && `${dataQa}-error-icon`}
          fill="currentColor"
        />
      }
    />
  )

  const wrapStyles = [sx.default.wrap, sx[kind].wrap, disabled && sx.disabled]

  return (
    <p
      {...dsUiDataProps}
      {...ariaProps(restProps)}
      {...dataProps(restProps)}
      data-qa={dataQa}
      css={wrapStyles}
      id={id}
    >
      {iconNode}
      {children}
    </p>
  )
}

InputDescription.kinds = inputDescriptionKinds

InputDescription.propTypes = {
  'aria-.*': PropTypes.string,
  children: PropTypes.node.isRequired,
  'data-qa': PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(inputDescriptionKinds),
}

InputDescription.displayName = 'InputDescription'
